// Copyright (c) Microsoft. All rights reserved.

import {
    AvatarProps,
    Divider,
    Persona,
    Text,
    ToggleButton,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import {
    ChevronDown20Regular,
    ChevronUp20Regular,
    ThumbDislike24Filled,
    ThumbLike24Filled,
} from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useChat } from '../../../libs/hooks/useChat';
import { AuthorRoles, ChatMessageType, IChatMessage, UserFeedback } from '../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { DefaultChatUser, FeatureKeys } from '../../../redux/features/app/AppState';
import { Breakpoints, customTokens } from '../../../styles';
import { timestampToDateString } from '../../utils/TextUtils';
import { PlanViewer } from '../plan-viewer/PlanViewer';
import { PromptDialog } from '../prompt-dialog/PromptDialog';
import DocumentViewer from '../shared/DocumentViewer';
import { TypingIndicator } from '../typing-indicator/TypingIndicator';
import * as utils from './../../utils/TextUtils';
import { ChatHistoryDocumentContent } from './ChatHistoryDocumentContent';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';
import { CitationCards } from './CitationCards';
import { UserFeedbackActions } from './UserFeedbackActions';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '75%',
        minWidth: '24em',
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
        ...shorthands.gap(customTokens.spacingHorizontalXS),
    },
    debug: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
    },
    alignEnd: {
        alignSelf: 'flex-end',
    },
    persona: {
        paddingTop: customTokens.spacingVerticalS,
    },
    item: {
        backgroundColor: customTokens.colorNeutralBackground1,
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...shorthands.padding(customTokens.spacingVerticalXS, customTokens.spacingHorizontalS),
    },
    me: {
        backgroundColor: customTokens.colorMeBackground,
        width: '100%',
    },
    time: {
        color: customTokens.colorNeutralForeground3,
        fontSize: customTokens.fontSizeBase200,
        fontWeight: 400,
    },
    header: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(customTokens.spacingHorizontalL),
    },
    canvas: {
        width: '100%',
        textAlign: 'center',
    },
    image: {
        maxWidth: '250px',
    },
    blur: {
        filter: 'blur(5px)',
    },
    controls: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: customTokens.spacingVerticalS,
        marginBottom: customTokens.spacingVerticalM,
        ...shorthands.gap(customTokens.spacingHorizontalL),
    },
    citationButton: {
        marginRight: 'auto',
    },
    rlhf: {
        marginLeft: 'auto',
    },
});

interface ChatHistoryItemProps {
    message: IChatMessage;
    messageIndex: number;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ message, messageIndex }) => {
    const classes = useClasses();
    const chat = useChat();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const [showCitationCards, setShowCitationCards] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<{ documentId: string } | null>(null);

    const isDefaultUser = message.userId === DefaultChatUser.id;
    const isMe = isDefaultUser || (message.authorRole === AuthorRoles.User && message.userId === activeUserInfo?.id);
    const isBot = message.authorRole === AuthorRoles.Bot;
    const user = isDefaultUser
        ? DefaultChatUser
        : chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
    const fullName = user?.fullName ?? message.userName;

    const avatar: AvatarProps = isBot
        ? { image: { src: conversations[selectedId].botProfilePicture } }
        : isDefaultUser
          ? { idForColor: selectedId, color: 'colorful' }
          : { name: fullName, color: 'colorful' };

    let content: JSX.Element;
    if (isBot && message.type === ChatMessageType.Plan) {
        content = <PlanViewer message={message} messageIndex={messageIndex} />;
    } else if (message.type === ChatMessageType.Document || message.type === ChatMessageType.URL) {
        content = (
            <>
                <ChatHistoryDocumentContent
                    isMe={isMe}
                    message={message}
                    onDocumentClick={(documentId: string) => {
                        setSelectedDocument({ documentId });
                    }}
                />
                {selectedDocument && (
                    <DocumentViewer
                        isOpen={!!selectedDocument}
                        onClose={() => {
                            setSelectedDocument(null);
                        }}
                        documentId={selectedDocument.documentId}
                        chatId={selectedId}
                    />
                )}
            </>
        );
    } else {
        content =
            isBot && message.content.length === 0 ? <TypingIndicator /> : <ChatHistoryTextContent message={message} />;
    }

    // TODO: [Issue #42] Persistent RLHF, hook up to model
    // Currently for demonstration purposes only, no feedback is actually sent to kernel / model
    const showShowRLHFMessage =
        //features[FeatureKeys.RLHF].enabled &&
        message.userFeedback === UserFeedback.Requested &&
        messageIndex === conversations[selectedId].messages.length - 1 &&
        message.prompt != '' &&
        message.userId === 'Bot';

    const messageCitations = message.citations ?? [];
    const showMessageCitation =
        messageCitations.length > 0 && message.citationsUsed && Object.keys(message.citationsUsed).length > 0;
    const showExtra = (showMessageCitation ?? showShowRLHFMessage) || showCitationCards;

    return (
        <div
            className={isMe ? mergeClasses(classes.root, classes.alignEnd) : classes.root}
            // The following data attributes are needed for CI and testing
            data-testid={`chat-history-item-${messageIndex}`}
            data-username={fullName}
            data-content={utils.formatChatTextContent(message.content)}
        >
            {
                <Persona
                    className={classes.persona}
                    avatar={avatar}
                    presence={
                        !features[FeatureKeys.SimplifiedExperience].enabled && !isMe
                            ? { status: 'available' }
                            : undefined
                    }
                />
            }
            <div className={isMe ? mergeClasses(classes.item, classes.me) : classes.item}>
                <div className={classes.header}>
                    {isBot && <Text weight="semibold">{conversations[selectedId].personaName ?? fullName}</Text>}
                    {!isMe && !isBot && <Text weight="semibold">{fullName}</Text>}
                    <Text className={classes.time}>{timestampToDateString(message.timestamp, true)}</Text>
                    {message.usedWebSearch && <span style={{ fontWeight: 'bold', color: '#00C389' }}>WebSearch Enabled</span>}
                    {isBot && <PromptDialog message={message} />}
                </div>
                {content}
                {showExtra && (
                    <div className={classes.controls}>
                        {showShowRLHFMessage && (
                            <div className={classes.rlhf}>
                                {<UserFeedbackActions messageIndex={messageIndex} messageId={message.id ?? ''} />}
                            </div>
                        )}
                        {showMessageCitation && (
                            <>
                                <Divider />
                                <Text size={600} weight="bold">
                                    Citations
                                </Text>
                                <ToggleButton
                                    appearance="primary"
                                    checked={showCitationCards}
                                    className={classes.citationButton}
                                    icon={showCitationCards ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                                    iconPosition="after"
                                    onClick={() => {
                                        setShowCitationCards(!showCitationCards);
                                    }}
                                    size="medium"
                                >
                                    {message.citationsUsed && Object.keys(message.citationsUsed).length > 0
                                        ? Object.keys(message.citationsUsed).length +
                                          ' ' +
                                          (Object.keys(message.citationsUsed).length === 1 ? 'citation' : 'citations')
                                        : ''}
                                </ToggleButton>
                            </>
                        )}
                        {showCitationCards && <CitationCards message={message} />}
                    </div>
                )}
                {message.userFeedback === UserFeedback.Positive && <ThumbLike24Filled color="green" />}
                {message.userFeedback === UserFeedback.Negative && <ThumbDislike24Filled color="red" />}
            </div>
        </div>
    );
};
